/**
 * AreaChart - smooth curved monthly trend with round circles + hover tooltip.
 *
 * Architecture:
 *   - SVG renders the line, area fill, and grid lines (these can stretch).
 *   - Dots (circles) are rendered as DOM divs positioned by percent —
 *     they stay perfectly round at any container width.
 *   - Tooltip on hover shows the month + value.
 */
import React, { useState } from "react";
import { useViewport } from "./useViewport.v2";
import { tokensV2 as T } from "./tokens.v2";

const COLOR = T.brand.base;
const FILL  = "rgba(252,128,25,0.12)";

function smoothPath(points) {
  if (points.length < 2) return "";
  let d = "M" + points[0].x + "," + points[0].y;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i];
    const p1 = points[i + 1];
    const cp1x = p0.x + (p1.x - p0.x) / 2;
    const cp1y = p0.y;
    const cp2x = p0.x + (p1.x - p0.x) / 2;
    const cp2y = p1.y;
    d += " C" + cp1x + "," + cp1y + " " + cp2x + "," + cp2y + " " + p1.x + "," + p1.y;
  }
  return d;
}

export function AreaChart({ data = [], prevLabel, height: heightProp, format = (v) => v.toLocaleString("en-IN"), summaryLabel, summaryValue, summaryDelta }) {
  const [hover, setHover] = useState(null);
  const { isMobile } = useViewport();
  if (!data || data.length < 2) return null;

  // Has-prev = at least one bucket carries a numeric `prev` value. If all
  // are null (e.g. till-date / lifetime) we render a single-series chart.
  const hasPrev = data.some(d => typeof d.prev === "number" && !Number.isNaN(d.prev));

  const height = heightProp || (isMobile ? 200 : 260);
  const padX = isMobile ? 14 : 56;
  const padY = isMobile ? 28 : 28;
  const width = 1200;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const values = data.map(d => d.value);
  // Y-axis must include both series so the prev line never gets clipped.
  const allValues = hasPrev
    ? values.concat(data.map(d => (typeof d.prev === "number" ? d.prev : null)).filter(v => v != null))
    : values;
  const max = Math.max(...allValues);
  const min = Math.min(...allValues);
  const span = Math.max(max - min, 1);
  const peakIdx = values.indexOf(Math.max(...values));

  const step = innerW / (data.length - 1);
  const points = data.map((d, i) => ({
    x: padX + i * step,
    y: padY + innerH - ((d.value - min) / span) * innerH,
    xPct: ((padX + i * step) / width) * 100,
    yPct: ((padY + innerH - ((d.value - min) / span) * innerH) / height) * 100,
    ...d,
  }));

  // Prev-period points (only if hasPrev). Buckets where prev is null are
  // skipped — the smooth-path helper requires every point to be defined,
  // so we filter to a contiguous tail-segment if needed.
  const prevPoints = hasPrev
    ? data
        .map((d, i) => {
          if (typeof d.prev !== "number") return null;
          const x = padX + i * step;
          const y = padY + innerH - ((d.prev - min) / span) * innerH;
          return {
            x, y,
            xPct: (x / width) * 100,
            yPct: (y / height) * 100,
            label: d.label,
            value: d.prev,
            i,
          };
        })
        .filter(Boolean)
    : [];

  const linePath = smoothPath(points);
  const fillPath = linePath + " L" + points[points.length - 1].x + "," + (padY + innerH) + " L" + points[0].x + "," + (padY + innerH) + " Z";
  const prevPath = prevPoints.length >= 2 ? smoothPath(prevPoints) : "";

  const ticks = isMobile ? 3 : 4;
  const tickRows = [];
  for (let i = 0; i <= ticks; i++) {
    const v = min + (span * i) / ticks;
    const y = padY + innerH - (i / ticks) * innerH;
    tickRows.push({ y, v, yPct: (y / height) * 100 });
  }

  const showHover = hover != null ? points[hover] : null;

  return (
    <div style={{
      width: "100%",
      backgroundColor: T.surface.card,
      borderRadius: 16,
      padding: 16,
      borderLeft: "4px solid " + COLOR,
      boxShadow: "0 1px 2px rgba(31,42,63,0.04)",
      boxSizing: "border-box",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12, gap: 10, flexWrap: "wrap", fontFamily: T.font.sans }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", minWidth: 0 }}>
          <span style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, color: T.text.primary, fontVariantNumeric: "tabular-nums", fontFeatureSettings: T.font.nums, letterSpacing: "-0.02em", whiteSpace: "nowrap" }}>
            {summaryValue || format(values[values.length - 1])}
          </span>
          {summaryDelta && (
            <span style={{ fontSize: 12, fontWeight: 700, color: T.status.good, fontVariantNumeric: "tabular-nums", fontFeatureSettings: T.font.nums, whiteSpace: "nowrap" }}>
              {summaryDelta}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
          {hasPrev && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 11, fontWeight: 600, color: T.text.muted, fontFamily: T.font.sans }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 14, height: 3, background: COLOR, borderRadius: 2 }} />
                Current
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 14, height: 0, borderTop: `2px dashed ${COLOR}`, opacity: 0.55 }} />
                {prevLabel || "Previous"}
              </span>
            </div>
          )}
          {!isMobile && (
            <span style={{ fontSize: 11, fontWeight: 600, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
              {summaryLabel || "Latest"}
            </span>
          )}
        </div>
      </div>

      <div style={{ position: "relative", width: "100%", height: height, fontFamily: T.font.sans }}>
        <svg viewBox={"0 0 " + width + " " + height} preserveAspectRatio="none"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}>
          {tickRows.map((t, i) => (
            <line key={i} x1={padX} y1={t.y} x2={width - padX} y2={t.y}
              stroke="rgba(31,42,63,0.06)" />
          ))}
          <path d={fillPath} fill={FILL} />
          {/* Previous-period overlay — rendered behind the current line so the
              "now" series stays visually dominant. Dashed + faded to read as
              "context, not the headline value". */}
          {prevPath && (
            <path d={prevPath} fill="none" stroke={COLOR} strokeOpacity="0.55"
              strokeWidth={isMobile ? "2.5" : "2"}
              strokeDasharray="6 5" strokeLinecap="round" strokeLinejoin="round"
              vectorEffect="non-scaling-stroke" />
          )}
          <path d={linePath} fill="none" stroke={COLOR} strokeWidth={isMobile ? "3.5" : "2.5"}
            strokeLinecap="round" strokeLinejoin="round"
            vectorEffect="non-scaling-stroke" />
        </svg>

        {!isMobile && tickRows.map((t, i) => (
          <span key={"y-" + i} style={{
            position: "absolute",
            left: 0, top: "calc(" + ((t.y / height) * 100) + "% - 7px)",
            width: padX - 8, textAlign: "right",
            fontSize: 10, fontWeight: 600, color: T.text.muted,
            fontFamily: T.font.mono,
            pointerEvents: "none",
          }}>{format(Math.round(t.v)).replace(/^Rs\s+/, "")}</span>
        ))}
        {(() => {
          // Adaptive label thinning with bullet-proof collision handling.
          // Strategy:
          //   1. Pick a stride based on label-width / container-width.
          //   2. Carve a "no-fly zone" of ±half-stride around peak/first/last
          //      and remove any strided labels that fall inside it. This
          //      prevents peak from sitting next to a stride-neighbour
          //      ("Mar 24 Mar 25" / "Mar 30 Mar 31" collisions).
          //   3. Final pass: enforce minimum percentage spacing between
          //      every accepted pair — forced labels (first/last/peak) win
          //      their conflicts so the highlight never disappears.
          const LABEL_W = isMobile ? 56 : 72;
          const containerWidthGuess = isMobile ? 360 : 1100;
          const maxLabels = Math.max(2, Math.floor(containerWidthGuess / LABEL_W));
          const stride = Math.max(1, Math.ceil(points.length / maxLabels));
          const lastIdx = points.length - 1;
          // No-fly zone: any strided index within `noFly` of a forced index
          // is suppressed up-front (don't even add to candidates).
          const noFly = Math.max(1, Math.floor(stride * 0.6));
          const inNoFly = (i) =>
            (i !== 0       && Math.abs(i - 0)        < noFly) ||
            (i !== lastIdx && Math.abs(i - lastIdx)  < noFly) ||
            (i !== peakIdx && Math.abs(i - peakIdx)  < noFly);
          const candidates = [];
          for (let i = 0; i < points.length; i++) {
            const forced = i === 0 || i === lastIdx || i === peakIdx;
            if (forced) candidates.push({ i, forced: true });
            else if (i % stride === 0 && !inNoFly(i)) candidates.push({ i, forced: false });
          }
          candidates.sort((a, b) => a.i - b.i);
          // Final pixel-distance pass — full label-width buffer (no 0.85x
          // shrink) so two labels never visually touch.
          const MIN_GAP = (LABEL_W / containerWidthGuess) * 100;
          const accepted = [];
          for (const c of candidates) {
            const px = points[c.i].xPct;
            const last = accepted[accepted.length - 1];
            if (!last || (px - points[last.i].xPct) >= MIN_GAP) {
              accepted.push(c);
              continue;
            }
            if (c.forced && !last.forced) accepted[accepted.length - 1] = c;
            // both-forced (rare: peak ≈ last): keep existing, drop new.
          }
          const visibleSet = new Set(accepted.map(a => a.i));
          return points.map((p, i) => {
            if (!visibleSet.has(i)) return null;
            return (
              <span key={"x-" + i} style={{
                position: "absolute",
                left: "calc(" + p.xPct + "% - 24px)",
                bottom: 0,
                width: 48, textAlign: "center",
                fontSize: 11,
                fontWeight: i === peakIdx ? 700 : 500,
                color: i === peakIdx ? COLOR : T.text.muted,
                fontFamily: T.font.sans,
                pointerEvents: "none",
                whiteSpace: "nowrap",
              }}>{p.label}</span>
            );
          });
        })()}

        {/* DOM-positioned dots — stay round regardless of SVG stretching.
            Partial buckets (current month/week/year still in progress) get a
            hollow + dashed border so the eye reads them as "not finished".
            HIT AREA is a fixed 20px box centered on the data point. The
            visible dot inside is sized to fit. Hover grows it via
            transform:scale() so the layout box never moves — this prevents
            the resize-feedback flicker that used to fire mouseleave the
            instant mouseenter changed the dot dimensions. */}
        {points.map((p, i) => {
          const isPeak = i === peakIdx;
          const isHover = i === hover;
          const isPartial = !!p.partial;
          const HIT = 20;                          // fixed hit-target size
          const baseDot = isPeak ? 12 : 8;         // visible dot size
          return (
            <span
              key={"hit-" + i}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              style={{
                position: "absolute",
                left: "calc(" + p.xPct + "% - " + (HIT / 2) + "px)",
                top: "calc(" + p.yPct + "% - " + (HIT / 2) + "px)",
                width: HIT, height: HIT,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
                zIndex: 2,
              }}
            >
              <span style={{
                width: baseDot, height: baseDot,
                borderRadius: "50%",
                backgroundColor: isPartial ? T.surface.card : (isPeak || isHover ? COLOR : T.surface.card),
                border: (isPartial ? "2.5px dashed " : "2.5px solid ") + COLOR,
                boxSizing: "border-box",
                // Visual grow via transform — does NOT resize the parent hit
                // area, so the mouse stays inside throughout the hover.
                transform: isHover ? "scale(1.45)" : "scale(1)",
                transformOrigin: "center",
                transition: "transform 140ms cubic-bezier(0.16, 1, 0.3, 1), background-color 140ms ease",
              }} />
            </span>
          );
        })}

        {/* Hover tooltip — when a comparison series is present, shows
            BOTH values + a Δ% so the user reads the difference directly. */}
        {showHover && (() => {
          const cur = showHover.value;
          const prv = typeof showHover.prev === "number" ? showHover.prev : null;
          const tipWidth = hasPrev ? 132 : 104;
          const tipHeightOffset = hasPrev ? 88 : 56;
          let deltaPct = null;
          if (hasPrev && prv != null && prv > 0) deltaPct = ((cur - prv) / prv) * 100;
          else if (hasPrev && prv === 0 && cur > 0) deltaPct = 100;
          const deltaColor = deltaPct == null
            ? "#A0A8B8"
            : deltaPct > 0 ? "#5BCB7A" : deltaPct < 0 ? "#FF7B7B" : "#A0A8B8";
          return (
            <div
              style={{
                position: "absolute",
                left: "calc(" + showHover.xPct + "% - " + (tipWidth / 2) + "px)",
                top: "calc(" + showHover.yPct + "% - " + tipHeightOffset + "px)",
                width: tipWidth, padding: "6px 10px",
                backgroundColor: "#1F2A3F",
                color: "#FFFFFF",
                borderRadius: 8,
                fontSize: 11, fontWeight: 700,
                fontFamily: T.font.sans,
                fontVariantNumeric: "tabular-nums",
                fontFeatureSettings: T.font.nums,
                textAlign: "center",
                boxShadow: "0 4px 14px rgba(31,42,63,0.18)",
                pointerEvents: "none",
                zIndex: 3,
              }}
            >
              <div style={{ fontSize: 10, opacity: 0.6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                {showHover.label}{showHover.partial ? " (so far)" : ""}
              </div>
              <div style={{ fontSize: 13, marginTop: 2 }}>{format(cur)}</div>
              {hasPrev && (
                <>
                  <div style={{ fontSize: 11, marginTop: 4, opacity: 0.7, fontWeight: 600 }}>
                    {prevLabel || "Previous"}: {prv != null ? format(prv) : "—"}
                  </div>
                  {deltaPct != null && (
                    <div style={{ fontSize: 11, marginTop: 2, color: deltaColor, fontWeight: 700 }}>
                      {(deltaPct > 0 ? "+" : "") + deltaPct.toFixed(1) + "%"}
                    </div>
                  )}
                </>
              )}
              <span style={{
                position: "absolute",
                left: "50%", bottom: -5,
                marginLeft: -5,
                width: 0, height: 0,
                borderLeft: "5px solid transparent",
                borderRight: "5px solid transparent",
                borderTop: "5px solid #1F2A3F",
              }} />
            </div>
          );
        })()}
      </div>
    </div>
  );
}
